import React from 'react';
import { CarouselTrack } from '../../../ui/Carousel/CarouselTrack';
import { CarouselItem } from '../../../ui/Carousel/CarouselItem';
import { CarouselIndicators } from '../../../ui/Carousel/CarouselIndicators';
import { useCarouselNavigation } from '../../../ui/Carousel/hooks/useCarouselNavigation';
import { CaretLeftIcon } from '../../../ui/icons/CaretLeftIcon';
import { CaretRightIcon } from '../../../ui/icons/CaretRightIcon';
import { FloorPlanCard } from './FloorPlanCard';
import type { Floorplan, Unit } from './UnitTable';
import * as styles from './unitsModule.styles';

type DesktopFloorPlanCarouselProps = {
  floorplans: Array<Floorplan>;
  onUnitSelect: (unit: Unit, floorplan: Floorplan) => void;
};

export const DesktopFloorPlanCarousel = ({ floorplans, onUnitSelect }: DesktopFloorPlanCarouselProps) => {
  const {
    trackRef,
    canScrollLeft,
    canScrollRight,
    currentPage,
    totalPages,
    handlePrevious,
    handleNext,
    scrollToPage,
    handleScroll,
  } = useCarouselNavigation({ gap: 'md', step: 1 });

  const showControls = floorplans.length > 2;
  const itemWidth = floorplans.length === 1 ? '100%' : showControls ? 'calc(100% - 48px)' : 'calc(50% - 8px)';

  return (
    <div className={styles.desktopCarouselWrapper}>
      <CarouselTrack ref={trackRef} gap="md" snapAlign="start" onScroll={handleScroll} aria-label="Floor plans">
        {floorplans.map((fp) => (
          <CarouselItem key={fp.modelId} width={itemWidth}>
            <FloorPlanCard floorplan={fp} onUnitSelect={onUnitSelect} compact />
          </CarouselItem>
        ))}
      </CarouselTrack>

      {showControls && (
        <div className={styles.desktopControlsOverlay}>
          <button
            type="button"
            className={styles.desktopControlButton}
            onClick={handlePrevious}
            disabled={!canScrollLeft}
            aria-label="Previous floor plans"
          >
            <CaretLeftIcon />
          </button>
          <button
            type="button"
            className={styles.desktopControlButton}
            onClick={handleNext}
            disabled={!canScrollRight}
            aria-label="Next floor plans"
          >
            <CaretRightIcon />
          </button>
        </div>
      )}

      {showControls && (
        <CarouselIndicators currentPage={currentPage} totalPages={totalPages} onPageSelect={scrollToPage} />
      )}
    </div>
  );
};
